/**
 * GIFT THRESHOLD SYSTEM
 * Ajoute/retire automatiquement un produit cadeau selon le montant du panier
 */

const GIFT_PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
};

// FILE D'ATTENTE GLOBALE pour coordonner les 3 instances
class GiftCoordinator {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
    this.instances = [];
  }

  register(instance) {
    this.instances.push(instance);
  }

  async scheduleCheck() {
    return new Promise((resolve) => {
      this.queue.push(resolve);
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.isProcessing || this.queue.length === 0) return;

    this.isProcessing = true;

    try {
      // Traiter TOUTES les instances en une seule passe
      await this.checkAllGifts();

      // Résoudre toutes les promesses en attente
      while (this.queue.length > 0) {
        const resolve = this.queue.shift();
        resolve();
      }
    } finally {
      this.isProcessing = false;

      // S'il y a encore des demandes en attente, les traiter
      if (this.queue.length > 0) {
        setTimeout(() => this.processQueue(), 0);
      }
    }
  }

  async checkAllGifts() {
    try {
      // Récupérer le panier UNE SEULE FOIS
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Calculer le total sans cadeaux
      let totalWithoutGift = 0;
      cart.items.forEach(item => {
        const isGift = item.properties && item.properties['_gift_threshold'];
        if (!isGift) {
          totalWithoutGift += item.final_line_price;
        }
      });

      // Vérifier chaque cadeau et collecter les actions nécessaires
      const actions = [];

      for (const instance of this.instances) {
        if (!instance.config || !instance.config.enabled) continue;

        const hasGift = cart.items.some(item => {
          const isGift = item.properties && item.properties['_gift_threshold'];
          const isGiftProduct = item.variant_id === instance.config.variantId;
          return isGift && isGiftProduct;
        });

        const shouldHaveGift = totalWithoutGift >= instance.config.threshold;

        if (shouldHaveGift && !hasGift) {
          actions.push({ type: 'add', instance });
        } else if (!shouldHaveGift && hasGift) {
          const giftItem = cart.items.find(item => {
            const isGift = item.properties && item.properties['_gift_threshold'];
            const isGiftProduct = item.variant_id === instance.config.variantId;
            return isGift && isGiftProduct;
          });
          actions.push({ type: 'remove', instance, giftItem });
        }
      }

      // Règles d'ajout auto par quantité d'un produit déclencheur
      this.collectQtyTriggerActions(cart, actions);

      // Exécuter toutes les actions collectées
      if (actions.length > 0) {
        await this.executeActions(actions);
      }
    } catch (error) {
      console.error('Gift coordinator error:', error);
    }
  }

  collectQtyTriggerActions(cart, actions) {
    const rules = window.themeQtyTriggers;
    if (!Array.isArray(rules) || rules.length === 0) return;

    for (const rule of rules) {
      // Liste des product IDs déclencheurs (collection = plusieurs ;
      // produit = un seul). Rétrocompat avec l'ancien champ singulier.
      const triggerIds = Array.isArray(rule.triggerProductIds)
        ? rule.triggerProductIds
        : (rule.triggerProductId != null ? [rule.triggerProductId] : []);

      // Quantité cumulée des produits déclencheurs (hors items auto-ajoutés)
      let triggerQty = 0;
      cart.items.forEach(item => {
        const isAuto = item.properties && (item.properties['_qty_trigger'] || item.properties['_gift_threshold']);
        if (!isAuto && triggerIds.indexOf(item.product_id) !== -1) {
          triggerQty += item.quantity;
        }
      });

      const addedItem = cart.items.find(item => {
        return item.properties
          && String(item.properties['_qty_trigger']) === String(rule.index)
          && item.variant_id === rule.addVariantId;
      });

      const shouldAdd = triggerQty >= rule.threshold;

      if (shouldAdd && !addedItem) {
        actions.push({ type: 'addQty', rule });
      } else if (!shouldAdd && addedItem) {
        actions.push({ type: 'remove', giftItem: addedItem });
      }
    }
  }

  async executeActions(actions) {
    // Exécuter les actions SÉQUENTIELLEMENT pour éviter les écritures
    // panier concurrentes (cause des erreurs 422 et de la désync du tiroir).
    let lastState = null;
    for (const action of actions) {
      let state = null;
      if (action.type === 'add') {
        state = await this.addGift(action.instance);
      } else if (action.type === 'addQty') {
        state = await this.addQtyProduct(action.rule);
      } else if (action.type === 'remove') {
        state = await this.removeGift(action.instance, action.giftItem);
      }
      if (state) lastState = state;
    }

    // UN SEUL re-render cohérent, à partir des sections renvoyées par la
    // dernière mutation (contenu + compteur + is-empty synchronisés).
    if (lastState) {
      this.renderFromState(lastState);
    }
  }

  // Renvoie la liste des sections à demander dans le body de la mutation.
  cartSections() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map((s) => s.id);
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  // POST une mutation panier en demandant les sections, et renvoie le JSON
  // (qui contient .sections + .item_count). Réutilise le pattern de
  // snippets/upsell-conditionnel.liquid.
  async applyCartMutation(url, fields) {
    const formData = new FormData();
    Object.keys(fields).forEach((k) => formData.append(k, fields[k]));
    formData.append('sections', this.cartSections().join(','));
    formData.append('sections_url', window.location.pathname);

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(`Cart mutation failed (${response.status})`);
    }
    return data;
  }

  async addGift(instance) {
    try {
      return await this.applyCartMutation('/cart/add.js', {
        id: instance.config.variantId,
        quantity: 1,
        'properties[_gift_threshold]': 'true',
        'properties[_gift_text]': instance.config.giftText || '🎁 CADEAU',
      });
    } catch (error) {
      console.error('Error adding gift:', error);
      return null;
    }
  }

  async addQtyProduct(rule) {
    try {
      return await this.applyCartMutation('/cart/add.js', {
        id: rule.addVariantId,
        quantity: 1,
        'properties[_qty_trigger]': String(rule.index),
      });
    } catch (error) {
      console.error('Error adding qty-trigger product:', error);
      return null;
    }
  }

  async removeGift(_instance, giftItem) {
    if (!giftItem) return null;
    try {
      return await this.applyCartMutation('/cart/change.js', {
        id: giftItem.key,
        quantity: 0,
      });
    } catch (error) {
      console.error('Error removing gift:', error);
      return null;
    }
  }

  // Re-render COMPLET et cohérent à partir d'un state /cart/*.js qui contient
  // .sections (HTML) et .item_count. Met à jour le contenu du tiroir, le
  // compteur, et synchronise is-empty — sans ouvrir le tiroir.
  renderFromState(state) {
    try {
      const sections = state.sections || {};
      const isEmpty = state.item_count === 0;

      // 1) Contenu du tiroir + compteur. On reproduit EXACTEMENT la logique de
      // cart-drawer.js renderContents (cible = selector, source = même selector
      // extrait du HTML), mais SANS appeler open().
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        cartDrawer.getSectionsToRender().forEach((section) => {
          const html = sections[section.id];
          if (!html) return;
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement) return;
          sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector || '.shopify-section');
        });
      }

      // 3) Synchroniser is-empty (cause de la désync / tiroir fantôme).
      if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);
      const drawerItems = document.querySelector('cart-drawer-items');
      if (drawerItems) drawerItems.classList.toggle('is-empty', isEmpty);
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
      const mainItems = document.getElementById('main-cart-items');
      if (mainItems) mainItems.classList.toggle('is-empty', isEmpty);

      // 4) Signaler la fin du refresh (source = nous, pour éviter la boucle).
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { source: 'gift-coordinator' },
      }));
    } catch (error) {
      console.error('Error rendering cart state:', error);
    }
  }

  async getCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  getSectionInnerHTML(html, selector) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : '';
  }
}

// Instance globale unique du coordinateur
const giftCoordinator = new GiftCoordinator();

class GiftThresholdManager {
  constructor(configName) {
    this.config = window[configName] || {};
    this.giftProperty = '_gift_threshold';

    if (!this.config.enabled || !this.config.productId || !this.config.variantId) {
      return;
    }

    // Enregistrer cette instance auprès du coordinateur
    giftCoordinator.register(this);
    this.init();
  }

  init() {
    // Vérifier au chargement
    this.checkAndUpdate();

    // Écouter les changements du panier
    document.addEventListener('cart:updated', (e) => {
      // Éviter les boucles infinies en ignorant nos propres événements
      if (e.detail && e.detail.source === 'gift-coordinator') {
        return;
      }
      this.checkAndUpdate();
    });

    // Déclenchement DIRECT dès qu'une écriture panier (ex: WideBundle) est
    // confirmée → évite le temps mort du MutationObserver (~300ms+).
    installCartWriteHook(() => this.checkAndUpdate());

    // Filet de sécurité : observer le tiroir pour les mises à jour qui ne
    // passeraient pas par fetch/XHR (debounce court).
    if (typeof MutationObserver !== 'undefined') {
      let debounceTimer;
      const observer = new MutationObserver(() => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          this.checkAndUpdate();
        }, 80);
      });

      const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('.cart-drawer');
      if (cartDrawer) {
        observer.observe(cartDrawer, { childList: true, subtree: true });
      }
    }
  }

  async checkAndUpdate() {
    // Déléguer au coordinateur qui gérera la synchronisation
    await giftCoordinator.scheduleCheck();
  }
}

// Écouteur dédié aux règles d'ajout par quantité (utile si aucun cadeau par montant n'est actif)
class QtyTriggerListener extends GiftThresholdManager {
  constructor() {
    super('__qtyTriggerNoConfig__'); // config inexistante → le parent ne s'auto-enregistre pas
    const rules = window.themeQtyTriggers;
    if (!Array.isArray(rules) || rules.length === 0) return;
    giftCoordinator.register({ config: null });
    this.init(); // this.checkAndUpdate existe désormais (héritée de GiftThresholdManager)
  }
}

// Intercepte les écritures panier (fetch + XHR) pour déclencher un check
// immédiatement après confirmation — y compris pour les apps tierces
// (WideBundle) qui n'émettent pas d'événement. Installé une seule fois ;
// notifie tous les abonnés enregistrés.
const cartWriteSubscribers = [];
function installCartWriteHook(callback) {
  if (typeof callback === 'function') cartWriteSubscribers.push(callback);
  if (window.__giftCartWriteHook) return;
  window.__giftCartWriteHook = true;

  const CART_WRITE = /\/cart\/(add|change|update|clear)/;
  const isOwnCall = (body) => {
    if (typeof body === 'string') {
      return body.indexOf('_qty_trigger') !== -1 || body.indexOf('_gift_threshold') !== -1;
    }
    if (body instanceof FormData) {
      return body.has('properties[_qty_trigger]') || body.has('properties[_gift_threshold]');
    }
    return false;
  };
  const notify = () => cartWriteSubscribers.forEach((cb) => {
    try { cb(); } catch (e) { /* noop */ }
  });

  const nativeFetch = window.fetch;
  window.fetch = function (...args) {
    const url = (args[0] && args[0].url) || args[0] || '';
    const body = args[1] && args[1].body;
    const watch = typeof url === 'string' && CART_WRITE.test(url) && !isOwnCall(body);
    const result = nativeFetch.apply(this, args);
    if (watch) result.then(() => notify(), () => {});
    return result;
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__giftWriteUrl = url;
    return nativeOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    const url = this.__giftWriteUrl || '';
    if (typeof url === 'string' && CART_WRITE.test(url) && !isOwnCall(body)) {
      this.addEventListener('load', notify);
    }
    return nativeSend.apply(this, arguments);
  };
}

function initGiftSystem() {
  new GiftThresholdManager('themeGiftThreshold');
  new GiftThresholdManager('themeGift2Threshold');
  new GiftThresholdManager('themeGift3Threshold');
  new QtyTriggerListener();
}

// Initialiser au chargement
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGiftSystem);
} else {
  initGiftSystem();
}

